import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  DEFAULT_PREVIEW_PAGE_COUNTS,
  ENUM_PROPERTY_VALUES,
  FACTOR_KIND_ORDER,
  ItemEnumProperty,
  MAX_QTY_SENTINEL,
  PropertyCharacteristic,
  RateConfig,
  RateConfigDocument
} from "../../../../model/rate/RateConfig";
import {
  PREVIEW_ITEM_DEFAULTS,
  PreviewItem,
  relevantNumericProperties,
  relevantPropertyCharacteristics,
  resolvePreviewQuantityTiers
} from "../../../../model/rate/PreviewMatrix";
import {RateConfigEvaluator, PreviewResult, BreakdownStep, OrderRateInput} from "../../../../model/rate/RateConfigEvaluator";
import {NUMBER_UP_VALUES, NumberUp} from "../../../../model/rate/numberUp";
import {LanguagePipe} from "../../../pipe/language.pipe";
import {PaperTypeService} from "../../../service-data/paper-type.service";
import {PackageTypeService} from "../../../service-data/package-type.service";
import {FacilityService} from "../../../service-data/facility.service";
import {CARRIER_SERVICE_LEVELS} from "../../../util/carrier-service-levels";
import {describeCharacteristicValue} from "../../../util/rate-characteristic-labels";

/**
 * One row of the preview demonstration: a concrete {@link PreviewItem}
 * plus the per-(pages × quantity-tier) cells (current evaluation +
 * optional delta vs. the last-saved baseline document).
 *
 * Pages no longer occupy a row dimension — they were promoted to the
 * column axis (one column-group per page-count, with the quantity-tier
 * columns repeated underneath each page-count group) so the user can
 * see how price varies with page count side-by-side instead of having
 * to scan vertically across N identical-looking rows whose only
 * difference is the "Pages" cell value.
 */
interface PreviewRow {
  /** The concrete enum combo + numberUp slice for this row (used for the row-axis cells). */
  item: PreviewItem;
  /** Display label for the enum combo (in the same order as the column headers below). */
  labels: { property: ItemEnumProperty; value: string; display: string; tooltip: string }[];
  /** Display label for the numberUp bucket (only included when `numberUp`
   *  is a relevant propertyCharacteristic). The row's `item.width`/`item.height`
   *  are synthesized so {@link computeNumberUp} returns this exact value. */
  numberUpLabel: string;
  /**
   * One cell per `(pageColumn × tierColumn)` data column, in row-major
   * order: cell index `i` corresponds to `pages = pageColumns[Math.floor(i / tierColumns.length)]`
   * and `tier = tierColumns[i % tierColumns.length]`. When the scope
   * doesn't depend on `pages` we synthesize a single page-column so
   * the indexing collapses to one tier-block (= legacy layout).
   */
  cells: PreviewCell[];
}

/**
 * Direction the price moved relative to the last-saved baseline:
 *  - `up`/`down`: numeric change in either direction (red/green respectively).
 *  - `new`: cell was previously infeasible, now has a finite price.
 *  - `removed`: cell was previously priced, now infeasible.
 */
export type DeltaDirection = 'up' | 'down' | 'new' | 'removed';

interface PreviewDelta {
  direction: DeltaDirection;
  /** Pre-formatted absolute change (e.g. "+0.12" / "−0.08" / "now ∞"). */
  absLabel: string;
  /** Pre-formatted percentage change (e.g. "+5.2%" / "−3.4%"); empty when not meaningful. */
  pctLabel: string;
}

/**
 * Per-peer comparison line. Renders the peer doc's value for the same
 * (item, qty) coordinate as the current cell, plus a delta vs. the
 * current rate. Peers are arbitrary additional `RateConfigDocument`s
 * supplied by the host (e.g. the rate configs of the facilities owned
 * by the SP whose `rateConfig` is being edited) so the user can see
 * how the current rate compares to each peer side-by-side, per cell.
 */
export type PeerDirection = 'up' | 'down' | 'same' | 'currentInfeasible' | 'peerInfeasible' | 'bothInfeasible';

interface PreviewPeerDelta {
  /** Human-readable label for the peer (shown beside the value). */
  label: string;
  /** Direction of the delta vs. the current rate. */
  direction: PeerDirection;
  /** Pre-formatted peer value (e.g. "1.25" or "∞"). */
  valueLabel: string;
  /** Pre-formatted absolute delta vs. current (e.g. "+0.20" / "−0.05"). */
  absLabel: string;
  /** Pre-formatted percentage delta vs. current. */
  pctLabel: string;
}

/** One pivot cell — current evaluation, plus optional baseline delta and per-peer comparisons. */
interface PreviewCell {
  current: PreviewResult;
  /** Undefined when no baseline is loaded, the baseline-cell matches exactly,
   *  or both sides are infeasible (so there's nothing to report). */
  delta?: PreviewDelta;
  /** Per-peer values + deltas. Includes the companion-cost document
   *  prepended as a synthetic peer when {@link PreviewMatrixComponent.showCompanionAsPeer}
   *  is on, followed by every entry in {@link PreviewMatrixComponent.peerDocs}. */
  peerDeltas: PreviewPeerDelta[];
  /** Pre-formatted display string for the cell's current value (e.g. "1.25" or "∞").
   *  Cached on the cell so the template doesn't re-call `cellDisplay()` per CD cycle. */
  display: string;
  /** Pre-formatted tooltip text (or '' when no tooltip applies). Cached so the
   *  `[matTooltip]` binding doesn't rebuild strings per CD cycle. */
  tooltip: string;
  /** True when the tooltip is meaningful and should be enabled. */
  tooltipEnabled: boolean;
}

/**
 * One per-FactorKind (or finalize) section in the per-cell breakdown
 * panel. Each group renders as a labeled subsection with its
 * contained {@link BreakdownFactor}s (or, for `finalize`, its
 * {@link BreakdownStep}s) and a per-group subtotal so the user can
 * read the math one bucket at a time ("here's what the per-book
 * factors added; here's what the per-page factors added; here are
 * the global adjustments").
 *
 * `kind` is the bucketing key:
 *  - one of the five `FactorKind`s when the contents came from real
 *    factors;
 *  - `'finalize'` when the contents are the synthetic global-minimum
 *    / percentage-discount / infeasibility steps the engine appends
 *    after iterating factors.
 *
 * `subtotalTarget` distinguishes which running total the bucket fed
 * into so the panel can format the subtotal correctly:
 *  - `'unit'`     — adds to per-book unit cost.
 *  - `'perOrder'` — adds once per order.
 *  - `'finalize'` — adjusts an already-running total; subtotal is
 *    skipped (the steps describe the net change inline).
 */
interface BreakdownGroup {
  kind: BreakdownStep['factorKind'] | 'finalize';
  /** Localized header label (e.g. "Per book", "Per page", "Finalize"). */
  label: string;
  /**
   * Per-factor blocks for factor-kind buckets. Each entry is one
   * `RateFactor` worth of math composed into a single inline
   * expression — see {@link BreakdownFactor}. Empty for the
   * `'finalize'` bucket (which uses {@link finalizeSteps} instead).
   */
  factors: BreakdownFactor[];
  /**
   * Raw breakdown steps for the `'finalize'` bucket only — these are
   * synthetic engine adjustments (global minimums, percentage
   * discount, infeasibility short-circuit) that don't slot into the
   * per-factor expression shape. Empty for every factor-kind bucket.
   */
  finalizeSteps: BreakdownStep[];
  /** Where this kind's contribution lands. */
  subtotalTarget: 'unit' | 'perOrder' | 'finalize';
  /**
   * Sum of every contained factor's `contribution`, in the units of
   * `subtotalTarget`. Zero when no factor contributed (still emitted
   * so the user sees the subtotal explicitly).
   */
  subtotal: number;
}

/**
 * One factor's contribution to a per-cell breakdown, composed into a
 * single inline math expression so the user can read the per-factor
 * computation top-down without having to mentally splice multiple
 * step lines together.
 *
 * Shape:
 *  - `parts[]` — one per matched cell. Each part carries the
 *    matched amount + the characteristic combo that matched + an
 *    optional inherited-from-key annotation. For factors with zero
 *    groups (PER_ITEM / PER_QUANTITY / PER_ORDER with just a
 *    `baseAmount`), `parts` is empty and {@link baseAmountPart}
 *    carries the single synthetic stand-in instead.
 *  - `baseAmountPart` — non-null when the factor has zero groups
 *    (uses `baseAmount` directly); mutually exclusive with a
 *    non-empty `parts` array.
 *  - `groupSum` — the sum of `parts[*].amount` (or
 *    `baseAmountPart.amount` when set). Equals the factor's
 *    `matchedAmount` from the engine's roll-up step.
 *  - `multiplier` + `multiplierLabel` — only set for
 *    `PER_NUMERIC_PROPERTY`. Renders as `groupSum × multiplier`.
 *  - `rawContribution` — `groupSum × (multiplier ?? 1)`. The amount
 *    BEFORE the per-factor minimum is applied.
 *  - `perItemMinimum` — set when the factor declares one. When the
 *    minimum actually bumped the contribution (i.e.
 *    `contribution !== rawContribution`), the template renders an
 *    extra "→ bumped by min {n}" segment.
 *  - `contribution` — the final amount added to the running total.
 *  - `targetTotal` — `'unit'` or `'perOrder'`. Drives the trailing
 *    `→ +X.XX unit cost` / `→ +X.XX per-order cost` label.
 *  - `note` — free-form explanatory text rendered below the
 *    expression (e.g. "No groups; uses baseAmount directly.",
 *    "No packageTypes supplied — factor contributed 0.").
 */
interface BreakdownFactor {
  factorId: string;
  factorKind: BreakdownStep['factorKind'];
  /** One entry per matched cell. Empty for "no-groups" factors —
   *  see {@link baseAmountPart} for the synthetic stand-in. */
  parts: BreakdownFactorPart[];
  /**
   * Synthetic stand-in for factors that have NO groups and use
   * `baseAmount` directly. Non-null only when `parts` is empty.
   */
  baseAmountPart: BreakdownFactorPart | null;
  /** Sum of `parts[*].amount` (or `baseAmountPart.amount` when set). */
  groupSum: number;
  /** Only set for PER_NUMERIC_PROPERTY factors. */
  multiplier?: number;
  /** Only set for PER_NUMERIC_PROPERTY factors. */
  multiplierLabel?: string;
  /** `groupSum × (multiplier ?? 1)` — amount BEFORE per-factor min. */
  rawContribution: number;
  /** Set when the factor declares a perItemMinimum. */
  perItemMinimum?: number;
  /** Final contribution after per-factor min. */
  contribution: number;
  /** Where this factor's contribution lands. */
  targetTotal: 'unit' | 'perOrder';
  /** Free-form explanatory text rendered below the expression. */
  note?: string;
}

/**
 * One matched-cell entry inside a {@link BreakdownFactor}. Each entry
 * represents one group resolution for the factor; multi-group factors
 * have multiple entries whose `amount`s sum into the factor's
 * `groupSum`. Factors with zero groups have an empty `parts[]` and a
 * `baseAmountPart` instead.
 *
 * For `PER_PACKAGE` factors with MULTIPLE groups, each top-level
 * part represents one package iteration and carries `subParts[]`
 * holding the per-group amounts that sum into the iteration's
 * `amount`. Single-group PER_PACKAGE factors collapse each iteration
 * to one part with no `subParts` (the part itself carries the
 * group's characteristics + matchedKey directly). Non-PER_PACKAGE
 * factors never use `subParts`.
 */
interface BreakdownFactorPart {
  /** Matched characteristics shown inside `[…]`, e.g. `bindingType|facility`. */
  characteristics: PropertyCharacteristic[];
  /** Matched cell key, e.g. `'COIL|*'` (or `''` when no group / baseAmount). */
  matchedKey: string;
  /** When inheritance applied, the exact key the user might have authored. */
  inheritedFromKey?: string;
  /** This group's matched amount. */
  amount: number;
  /**
   * For `PER_PACKAGE` factors' per-package iteration steps the engine
   * emits one entry per element of `packageTypes[]`. The note clarifies
   * which package index/id the amount came from so the inline math
   * reads sensibly. Undefined for every other factor kind.
   */
  perPackageNote?: string;
  /**
   * Optional inner expansion — present only for PER_PACKAGE iterations
   * of factors with MULTIPLE groups. Each entry is one group's matched
   * cell for THIS package iteration; the outer `amount` is their sum.
   * The UI renders sub-parts as `( [chars1=key1] amt1 + [chars2=key2]
   * amt2 ) = amount` underneath the package label so the user can see
   * which group contributed what to each package's total.
   */
  subParts?: BreakdownFactorPart[];
}

/**
 * Renders a read-only "demonstration" table for the current scope:
 * a cartesian product of the enum-valued propertyCharacteristics that
 * the scope's factors actually mention (× the document's
 * `_previewPageCounts` when factors depend on `pages`), evaluated
 * against each defaultQuantityBreak tier.
 *
 * The model-layer helpers live in `src/model/rate/PreviewMatrix.ts`;
 * this component supplies the live `paperType` IDs (which aren't
 * known statically) and feeds rows through
 * {@link RateConfigEvaluator.evaluateBreaks}.
 *
 * View-only — the component takes no inputs that mutate. Re-renders
 * whenever `doc`, `scope`, or `config` change (the parent ScopePanel
 * passes the canonical doc + the same active config it gives the
 * other panels).
 */
@Component({
  selector: 'app-rate-preview-matrix',
  templateUrl: './preview-matrix.component.html',
  styleUrls: ['./preview-matrix.component.css']
})
export class PreviewMatrixComponent implements OnInit, OnChanges {
  @Input() doc: RateConfigDocument = {};
  @Input() scope: string = '';
  @Input() config: Partial<RateConfig> = {};
  @Input() currency: string = 'USD';
  @Input() readOnly: boolean = false;
  /**
   * Last-saved snapshot of the document. When provided AND the same scope
   * has factors in it, each cell shows the delta vs. its baseline value.
   * Pass `{}` (or omit) to disable the delta column.
   */
  @Input() baselineDoc: RateConfigDocument = {};
  /**
   * Companion-cost document. When the editor is editing the SP-side
   * `rateConfig` (what the SP charges customers), this is the matching
   * `serviceProviderChargedRateConfig` (what the network charges the
   * SP). Each cell renders a third line showing the margin — the
   * difference between the current rate and the cost, with green for
   * profit and red for loss. Pass `{}` (or omit) to disable the
   * margin column.
   */
  @Input() companionDoc: RateConfigDocument = {};
  /**
   * Human-readable label for the companion cost (used in tooltips
   * and the legend). Defaults to "cost"; the editor sets it to
   * something more specific when known (e.g. "network cost").
   */
  @Input() companionLabel: string = '';

  /**
   * Optional peer documents to compare against per cell. Each entry's
   * `doc` is evaluated for the same (item, quantity) coordinate as
   * the current rate; the result is rendered as an extra line under
   * the current value with the peer's `label`. Used by the
   * Network-Settings SP `rateConfig` editor to surface side-by-side
   * comparisons with the rate configs of the SP's facilities — so the
   * user can see how the SP-charged rate compares to each facility's
   * own rate at every cell, in one place.
   */
  @Input() peerDocs: { label: string; doc: RateConfigDocument }[] = [];

  /**
   * Catalog of facilities the user can choose from (rendered as a
   * multi-select inline with the page-counts field). The component
   * itself doesn't load facilities — the host fills this in. When
   * empty, the picker is hidden.
   */
  @Input() availableFacilities: { id: number; label: string }[] = [];
  /** Currently-picked facility ids; emitted on change. */
  @Input() selectedFacilityIds: number[] = [];
  @Output() selectedFacilityIdsChange = new EventEmitter<number[]>();

  /**
   * Whether the companion-cost document (e.g. the SP's
   * `serviceProviderChargedRateConfig`) should be rendered as a
   * synthetic peer line in every cell. Defaults to `true` so the
   * existing UX shows the comparison out of the box; the in-preview
   * toggle lets the user hide it without losing the loaded data.
   */
  @Input() showCompanionAsPeer: boolean = true;
  @Output() showCompanionAsPeerChange = new EventEmitter<boolean>();

  /**
   * True when the host editor is editing a packing rate-config
   * variant (`packingRateConfig`, `serviceProviderChargedPackingRateConfig`,
   * `packingRateConfigOverride`). Gates the order-level "Packing
   * preview" subsection — production rate-configs evaluate per-item,
   * never per-order, so a packing preview against a production doc
   * would silently report all zeros (or be visually correct but
   * conceptually misleading: production scopes don't carry
   * `packageTypes[]` / `carrierServiceLevel` at runtime).
   *
   * Defaulted to `false` so existing hosts that haven't opted in keep
   * the previous behavior (packing-preview hidden); the parent scope
   * panel passes it through from the outer editor's getter.
   */
  @Input() isPackingConfig: boolean = false;

  /** Emitted when the user edits the comma-separated preview page counts. */
  @Output() pageCountsChange = new EventEmitter<number[] | undefined>();

  /** True when {@link baselineDoc} carries any factors for {@link scope}; drives legend visibility. */
  hasBaseline: boolean = false;
  /**
   * True when the host wants the companion comparison surfaced — driven
   * by whether a non-empty `companionLabel` has been supplied. We use
   * the LABEL (not the doc contents) because the editor only sets the
   * label after deciding a companion comparison applies to this editing
   * context (i.e. SP `rateConfig`); checking doc contents would hide the
   * toggle whenever the SP simply hasn't authored a charged-by-network
   * doc yet, which is exactly when the user most needs to see the
   * comparison ("nothing here yet" is itself meaningful information).
   */
  hasCompanion: boolean = false;
  /** True when {@link companionDoc} carries any factors for {@link scope};
   *  used to gate the legend hint and the "rebuild" peer-line work. */
  hasCompanionFactors: boolean = false;

  /** Column headers (one per quantity tier). */
  tierColumns: { minQty: number; maxQty: number; label: string }[] = [];
  /**
   * Per-page-count column groups. Each entry is one outer column-group
   * that spans {@link tierColumns}.length data columns. Populated from
   * `doc._previewPageCounts` (or {@link DEFAULT_PREVIEW_PAGE_COUNTS})
   * when {@link pagesIsRelevant} is true; collapses to a single entry
   * with an empty label when pages aren't relevant (so the header
   * grid stays one row tall and the cell-index math
   * `i = pageIdx * tierColumns.length + tierIdx` keeps working).
   *
   * Splitting pages onto the COLUMN axis (instead of the row axis it
   * used to live on) lets the user compare unit cost across page-counts
   * side-by-side within a single row, instead of scrolling vertically
   * across N rows whose only difference was the Pages cell value —
   * which previously had no header on the right-hand tier columns and
   * looked like duplicate unheadered columns in the wild.
   */
  pageColumns: { pages: number; label: string }[] = [];
  /** Data rows, one per (enum-combo × numberUp) tuple. */
  rows: PreviewRow[] = [];
  /** Which enum properties are columns (subset of the four enum-valued chars in scope-relevant order). */
  enumColumns: ItemEnumProperty[] = [];
  /** True when the scope has any factors depending on `pages` (drives the "Pages" column visibility). */
  pagesIsRelevant: boolean = false;
  /** True when the scope has any factor group conditioned on `numberUp`
   *  (drives the "Number up" column visibility and the per-row enumeration
   *  of `2UP`/`4UP`). */
  numberUpIsRelevant: boolean = false;
  /**
   * True when the scope has any factor group conditioned on
   * `quantityBreak`. Drives the per-tier column rendering — when
   * `false` the matrix collapses to a SINGLE price column because
   * every tier would render the same `unitCost` (only
   * `quantityBreak`-conditioned cells differentiate by tier).
   * The engine still evaluates against a representative tier, so
   * `PER_QUANTITY` / `PER_ITEM` / `PER_NUMERIC_PROPERTY` factors
   * continue to contribute their per-book amount to the cell.
   */
  quantityBreakIsRelevant: boolean = false;
  /** Human-readable note shown when the table is empty (no factors authored). */
  emptyNote: string = '';

  // ── Per-cell breakdown panel ─────────────────────────────────────
  //
  // When the user clicks a cell, we compute a step-by-step trace of
  // every factor's contribution via {@link RateConfigEvaluator.evaluateItemBreakdown}
  // and surface it in a detail panel below the table. The panel
  // displays which cell was selected (row coordinate + quantity tier)
  // and lists each step with: factor id+kind, matched cell key, raw
  // amount, multiplier (for PER_NUMERIC_PROPERTY factors), per-factor
  // minimum bumps, the running total side it affected, and any notes
  // (e.g. infeasibility, no-match-zero, discount/minimum bumps).
  //
  // Computed on demand on click — not eagerly during `rebuild()` — so
  // the preview's main render stays cheap when only a few cells need
  // explanation.

  /** Index of the currently selected (rowIndex, cellIndex) tuple, or null when nothing is selected.
   *  `rowIndex` indexes into the FULL {@link rows} array (not {@link filteredRows}). */
  selectedCell: { rowIndex: number; cellIndex: number } | null = null;
  /**
   * Per-FactorKind grouped breakdown for the UI. Each bucket carries
   * its localized label + the per-factor blocks that belong to it
   * (folded from the engine's per-group "line item" + roll-up steps,
   * see {@link buildBreakdownGroups}) + the bucket's cumulative
   * contribution to `unit` or `perOrder`. Built from the engine's
   * raw step list in {@link onCellClick}; rebuilt on every cell click
   * so the template iterates a stable array reference (rather than
   * calling a getter that filters per CD pass — same memoization
   * pattern as the rest of this component).
   *
   * The bucket order mirrors {@link FACTOR_KIND_ORDER} (PER_ORDER,
   * PER_ITEM, PER_QUANTITY, PER_NUMERIC_PROPERTY, PER_PACKAGE) so the
   * breakdown reads top-down in the same order the factor-kind
   * sections appear in the scope editor. A trailing `finalize`
   * bucket holds the global-minimum / percentage-discount /
   * infeasibility steps so they remain distinct from the
   * factor-attributable math.
   */
  selectedBreakdownGroups: BreakdownGroup[] = [];
  /** Final cell totals shown beneath the per-kind breakdown groups. */
  selectedBreakdownTotals: { unitCost: number; perOrderCost: number; totalCost: number; quantity: number; infeasible: boolean } | null = null;
  /** Display label for the selected cell's coordinate (row chars + tier). */
  selectedCellLabel: string = '';

  // ── Header column filters + hide-empty toggle ───────────────────
  //
  // Each enum-valued column header gets a multi-select dropdown
  // populated with the distinct values that actually appear in the
  // generated rows for that column. Picking values restricts the
  // visible rows; empty selection means "all values" (no filter).
  // The `hideEmptyRows` toggle suppresses rows whose every cell is
  // infeasible (∞) so users can focus on producible combos. Both
  // filter sets are applied lazily via {@link filteredRows}.

  /** Per-column distinct values (in render order); rebuilt with {@link rows}. */
  columnFilterOptions: { property: ItemEnumProperty; values: { raw: string; display: string }[] }[] = [];
  /** Currently-selected raw values per enum column ('' for unset). Empty array = "all". */
  columnFilters: { [k in ItemEnumProperty]?: string[] } = {};
  /** When true, rows whose every cell is infeasible (∞) are hidden. */
  hideEmptyRows: boolean = false;

  /**
   * Memoized output of {@link applyFilters}. The template iterates this
   * field directly (NOT a getter) so Angular's change detection doesn't
   * re-walk every row × column on every tick — that loop is what made
   * mat-tooltip / mat-select dropdowns feel sluggish.
   */
  filteredRows: { row: PreviewRow; rowIndex: number }[] = [];
  /** Total `<td>` cells per row — cached for the inline-breakdown row's colspan. */
  totalColumns: number = 0;

  readonly localizeNumber = (n: any) => LanguagePipe.localizeNumber(n);

  // ── Packing-preview state (declared up here for TSLint member-ordering;
  //    the methods that operate on them live near the bottom of the class). ──

  /** Static carrier service level options for the dropdown (mirrors the chip set). */
  readonly carrierServiceLevels = CARRIER_SERVICE_LEVELS;

  /** True when the scope has any PER_PACKAGE factor — drives the section's visibility. */
  packingPreviewRelevant: boolean = false;

  /**
   * Per-control visibility flags for the packing preview. Each is true
   * only when the active scope's factors actually reference the
   * corresponding dimension — keeps the panel from asking the user
   * for inputs the engine will silently ignore. See the
   * `rebuild()` block that sets these alongside
   * {@link packingPreviewRelevant} for the exact derivation.
   */
  packingPreviewShowPackageTypes: boolean = false;
  packingPreviewShowCarrier: boolean = false;
  packingPreviewShowQuantity: boolean = false;
  /**
   * True when at least one group in the active scope conditions on
   * `facility` — drives the packing preview's "Facility" dropdown
   * visibility. The dropdown lists every active facility from the
   * live {@link FacilityService} catalog, plus a "(none)" sentinel
   * that resolves to the empty string (only matches wildcard cells).
   */
  packingPreviewShowFacility: boolean = false;

  /** Raw text input value for the comma-separated packageTypes[] field. */
  packagePreviewPackageTypesRaw: string = '';
  /** Selected carrier service level id (e.g. 'UPS_GROUND'). Empty means "no carrier". */
  packagePreviewCarrierServiceLevel: string = '';
  /** Book quantity used for the order-level preview. Defaults to 1. */
  packagePreviewQuantity: number = 1;

  /** Current order-level evaluation result. Recomputed on every input change. */
  packagePreviewResult: PreviewResult | null = null;
  /**
   * Per-FactorKind breakdown of the packing preview, built from
   * {@link RateConfigEvaluator.evaluateOrderBreakdown}'s steps via the
   * same {@link buildBreakdownGroups} helper that powers the per-cell
   * breakdown in the rate table. Rendered as a collapsible "How this
   * was calculated" section under the packing-preview result so the
   * user can see each factor kind's contribution (and per-package
   * iteration sub-steps for `PER_PACKAGE` factors) without having to
   * mentally diff the per-book matrix against the order-level total.
   */
  packagePreviewBreakdownGroups: BreakdownGroup[] = [];
  /** Toggle for the packing-preview breakdown panel. Defaults to hidden
   *  so the result summary stays compact until the user opts in. */
  packagePreviewBreakdownExpanded: boolean = false;
  /** Parsed packageTypes[] array; cached so the template can show what was actually used. */
  packagePreviewPackageTypes: string[] = [];
  /** Any parse warning for the packageTypes field (empty when input is clean). */
  packagePreviewParseWarning: string = '';

  /** Selected facility id for the packing preview's `OrderRateInput.facility`. Empty = none. */
  packagePreviewFacility: string = '';

  /**
   * Memoized options for the packing-preview "Facility" `<mat-select>`.
   *
   * MUST be a plain field (not a getter). Angular change detection
   * evaluates template `*ngFor` source expressions on every CD pass;
   * a getter that returns a freshly-built array of freshly-built
   * objects each time makes the `IterableDiffer` see every option as
   * "new" on every tick, tearing down and re-rendering every
   * `<mat-option>` in the dropdown. With dozens of facilities and the
   * mat-select's overlay subscribing to the option set, this caused
   * the entire tab to freeze the moment a factor was conditioned on
   * `facility` (which is precisely when this dropdown mounts). Same
   * memoization pattern used for {@link RateConfigEditorComponent#availableFacilityChoices}.
   *
   * Refreshed from {@link refreshActiveFacilityOptions} whenever
   * {@link FacilityService.objects} actually changes (initial load,
   * the on-demand `getItems()` subscription in `ngOnInit`, and once
   * per `rebuild()` so notifications-driven updates pick up promptly).
   */
  activeFacilityOptions: { id: string; label: string }[] = [];

  constructor(public paperTypeService: PaperTypeService,
              public packageTypeService: PackageTypeService,
              public facilityService: FacilityService) {}

  ngOnInit(): void {
    // PaperType / PackageType / Facility IDs are live data; we need
    // them as paperType / packageType / facility axis values. Per
    // AGENTS.md "On-Demand Data Loading", components load on access.
    // The packing rate-config editor host uses packageType as a matrix
    // axis the same way the production rate-config editor uses
    // paperType; the network-level packing rate-config additionally
    // uses `facility` to differentiate per-facility — whichever
    // document is being edited, the right chip set is ready.
    if (!this.paperTypeService.objects || this.paperTypeService.objects.length === 0) {
      this.paperTypeService.getItems().subscribe(() => this.rebuild());
    }
    if (!this.packageTypeService.objects || this.packageTypeService.objects.length === 0) {
      this.packageTypeService.getItems().subscribe(() => this.rebuild());
    }
    if (!this.facilityService.objects || this.facilityService.objects.length === 0) {
      this.facilityService.getItems().subscribe(() => {
        // Refresh the memoized facility dropdown options before
        // rebuilding so the packing-preview <mat-select> picks up the
        // freshly-loaded catalog without re-running the getter on every
        // CD tick. See {@link activeFacilityOptions} for the rationale.
        this.refreshActiveFacilityOptions();
        this.rebuild();
      });
    } else {
      // Catalog already loaded — seed the memoized options immediately
      // so the first rebuild() sees a populated dropdown.
      this.refreshActiveFacilityOptions();
    }
    this.rebuild();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.rebuild();
  }

  /** Recompute the matrix. Cheap enough to run on every config change. */
  private rebuild(): void {
    // Keep the packing-preview's facility <mat-select> options in sync
    // with the live FacilityService catalog. The refresh is a no-op
    // when nothing changed (it diffs the existing memoized list), so
    // it's safe to call on every rebuild tick — and putting it here
    // means notifications-driven catalog updates (e.g. a facility
    // toggled active by another user) propagate without waiting for
    // a fresh getItems() call. See {@link activeFacilityOptions}.
    this.refreshActiveFacilityOptions();
    this.tierColumns = [];
    this.rows = [];
    this.enumColumns = [];
    this.pagesIsRelevant = false;
    this.numberUpIsRelevant = false;
    this.quantityBreakIsRelevant = false;
    this.emptyNote = '';
    // Clear any per-cell breakdown — old indices are no longer valid
    // against the freshly-built rows.
    this.selectedCell = null;
    this.selectedBreakdownGroups = [];
    this.selectedBreakdownTotals = null;
    this.selectedCellLabel = '';

    // Packing preview is relevant when the scope has a PER_PACKAGE
    // factor or any group conditioned on `carrierServiceLevel` /
    // `facility`. We compute this BEFORE the early-return guards below
    // so the section can still render an empty-state placeholder when
    // there are no factors yet.
    //
    // ADDITIONALLY gated on {@link isPackingConfig} — the order-level
    // `evaluateOrder` entry point only makes sense for packing rate-
    // configs (the production layer evaluates per-item and never
    // receives `packageTypes[]` / `carrierServiceLevel` at runtime).
    // Showing the Packing preview on an SP / facility production
    // `rateConfig` confused operators into thinking the production
    // side could be priced order-at-a-time, which it cannot.
    //
    // The individual packing-preview CONTROLS (package types,
    // carrier service level, book quantity, facility) are additionally
    // gated by what the active factors actually reference, so the user
    // isn't asked for inputs the engine will ignore. Mirrors the
    // same principle that already governs `pagesIsRelevant` /
    // `numberUpIsRelevant` / column enumeration: only surface a
    // dimension when at least one factor's group depends on it (or
    // when a kind of factor that scales with that dimension exists).
    const allFactors = (this.config && this.config.factors) || [];
    const usedCharsForPacking = relevantPropertyCharacteristics(this.config);
    const hasPerPackage = allFactors.some(f => f.kind === 'PER_PACKAGE');
    const hasPerBookScaling = allFactors.some(f =>
      f.kind === 'PER_ITEM' || f.kind === 'PER_QUANTITY' || f.kind === 'PER_NUMERIC_PROPERTY');
    this.packingPreviewRelevant = this.isPackingConfig && (
      hasPerPackage
      || usedCharsForPacking.indexOf('carrierServiceLevel') >= 0
      || usedCharsForPacking.indexOf('facility') >= 0
    );
    // Per-control gating inside the packing preview:
    //  - `packageTypes` input only when there's a PER_PACKAGE factor
    //    or some group depends on `packageType`.
    //  - carrier dropdown only when some group depends on
    //    `carrierServiceLevel`.
    //  - quantity field only when something scales with quantity (a
    //    per-book factor, or a `quantityBreak`-conditioned group).
    //  - facility dropdown only when some group depends on `facility`.
    this.packingPreviewShowPackageTypes  = hasPerPackage || usedCharsForPacking.indexOf('packageType') >= 0;
    this.packingPreviewShowCarrier       = usedCharsForPacking.indexOf('carrierServiceLevel') >= 0;
    this.packingPreviewShowQuantity      = hasPerBookScaling || usedCharsForPacking.indexOf('quantityBreak') >= 0;
    this.packingPreviewShowFacility      = usedCharsForPacking.indexOf('facility') >= 0;
    // Recompute the packing preview result whenever the doc/scope
    // changes — the existing inputs may now resolve to different cells.
    if (this.packingPreviewRelevant) { this.rebuildPackagePreview(); } else { this.packagePreviewResult = null; this.packagePreviewBreakdownGroups = []; }

    if (!this.config || !this.scope) { return; }

    if (allFactors.length === 0) {
      this.emptyNote = LanguagePipe.get('No factors yet — add a factor to populate the demonstration.', 'compressSpaces');
      return;
    }
    const factors = allFactors;

    // Which enum-valued propertyCharacteristics matter for THIS scope.
    // `carrierServiceLevel` is NOT enumerated here — it is an order-
    // level constant. The per-book preview can't surface PER_PACKAGE
    // factors or `carrierServiceLevel`-keyed cells meaningfully; the
    // separate Packing preview section (above) handles those by
    // calling `RateConfigEvaluator.evaluateOrder()` with a
    // user-supplied OrderRateInput.
    //
    // `facility` IS enumerated as a per-book column when a per-book
    // factor (PER_ITEM / PER_QUANTITY / PER_NUMERIC_PROPERTY) groups
    // on it — the evaluator reads `facility` from each row's item, so
    // the matrix can demonstrate per-facility differentiation just
    // like it does for interiorType / paperType etc. PER_PACKAGE-only
    // facility differentiation is exclusively surfaced in the Packing
    // preview below (which has a facility dropdown that feeds
    // `OrderRateInput.facility`).
    const usedChars = relevantPropertyCharacteristics(this.config);
    this.enumColumns = (['interiorType', 'bindingType', 'laminateType', 'paperType', 'packageType', 'facility'] as ItemEnumProperty[])
      .filter(p => usedChars.indexOf(p) >= 0);
    this.numberUpIsRelevant = usedChars.indexOf('numberUp') >= 0;
    this.quantityBreakIsRelevant = usedChars.indexOf('quantityBreak') >= 0;

    // Pages axis: include only when at least one factor depends on `pages`.
    const numericProps = relevantNumericProperties(this.config);
    this.pagesIsRelevant = numericProps.indexOf('pages') >= 0;
    const pageCounts: number[] = this.pagesIsRelevant
      ? ((this.doc && Array.isArray(this.doc._previewPageCounts) && this.doc._previewPageCounts.length > 0)
          ? this.doc._previewPageCounts
          : [48, 96, 200, 400, 700])   // mirrors DEFAULT_PREVIEW_PAGE_COUNTS
      : [PREVIEW_ITEM_DEFAULTS.thickness ? 200 : 200];

    // Quantity tier columns.
    //
    // When `quantityBreak` is not referenced by any factor group, all
    // tier columns would resolve to the same `unitCost` (only
    // `quantityBreak`-conditioned cells differentiate by tier), so we
    // collapse to a SINGLE representative "Price" column instead of
    // rendering ten identical tiers. The collapsed column still
    // evaluates against a real quantity tier (the first / lowest one)
    // so `PER_QUANTITY` / `PER_ITEM` factors contribute their amount
    // correctly. This mirrors how the per-row preview already
    // suppresses the "Pages" / "Number up" columns when those
    // characteristics aren't used.
    const tiers = resolvePreviewQuantityTiers(this.config);
    if (this.quantityBreakIsRelevant) {
      this.tierColumns = tiers.map(t => ({
        minQty: t.minQty,
        maxQty: t.maxQty,
        label: t.maxQty >= MAX_QTY_SENTINEL
          ? this.localizeNumber(t.minQty) + '+'
          : this.localizeNumber(t.minQty) + '–' + this.localizeNumber(t.maxQty)
      }));
    } else {
      // Use the first available tier as the representative quantity.
      // Defensive fallback to (minQty=1, maxQty=∞) when the scope has
      // somehow zero tiers (engine defaults apply anyway).
      const t = tiers && tiers.length > 0 ? tiers[0] : {minQty: 1, maxQty: MAX_QTY_SENTINEL};
      this.tierColumns = [{
        minQty: t.minQty,
        maxQty: t.maxQty,
        label: LanguagePipe.get('Price', 'compressSpaces')
      }];
    }
    // Tier list passed to the engine — MUST match `tierColumns` 1:1 so
    // the per-cell loop in the row builder below emits exactly one
    // result per visible tier column. Earlier revisions forwarded the
    // FULL `tiers` list (the scope's `defaultQuantityBreaks` or engine
    // defaults) regardless of whether the scope actually grouped on
    // `quantityBreak`, which made `evaluateBreaks(...)` return 7
    // results (all identical, since no factor differentiated by
    // quantity) per page-column. When pages lived on the row axis
    // that surfaced as 7 cells under a single "Price" header — easy
    // to miss; after the pages → column-axis migration it surfaced as
    // 7 repeated rate cells inside every page-count group, matching
    // the user's "the cell is repeated N times for each page count"
    // bug report. Building one tier per visible column eliminates the
    // duplication and keeps the column count honest.
    const evalTiers: { minQty: number; maxQty: number }[] =
      this.tierColumns.map(tc => ({minQty: tc.minQty, maxQty: tc.maxQty}));

    // Build the page-column axis. Pages used to be a ROW dimension —
    // we'd push one row per `(combo, pages)` and the pages value appeared
    // as a value-cell in a "Pages" row-axis column. That visualization
    // scaled poorly: with 5 default page counts and any meaningful combo
    // count, the user scrolled vertically across N rows whose only
    // difference was the Pages cell value, and the tier columns to the
    // right had no per-page header to distinguish them, which read as
    // "duplicate columns with no header" in user feedback.
    //
    // The cleaner pivot — and the one the user requested — is to put
    // pages on the COLUMN axis: each page-count becomes a column-group
    // header that spans the underlying tier columns. When pages aren't
    // relevant to this scope we collapse to a single placeholder column
    // so the cell-index math
    // (`cellIndex = pageIdx * tierColumns.length + tierIdx`)
    // continues to work unchanged for the legacy single-page-count case.
    this.pageColumns = this.pagesIsRelevant
      ? pageCounts.map(p => ({pages: p, label: this.localizeNumber(p)}))
      : [{pages: pageCounts[0], label: ''}];

    // Cartesian product across the relevant enum columns.
    const valueListsPerCol: { prop: ItemEnumProperty; values: string[] }[] = this.enumColumns.map(p => ({
      prop: p,
      values: this.valuesFor(p)
    }));
    let combos: { [k in ItemEnumProperty]?: string }[] = [{}];
    for (const e of valueListsPerCol) {
      if (e.values.length === 0) { continue; }
      const next: { [k in ItemEnumProperty]?: string }[] = [];
      for (const acc of combos) {
        for (const v of e.values) { next.push({...acc, [e.prop]: v}); }
      }
      combos = next;
    }

    // Determine whether the baseline document has anything to compare against.
    const baselineScope = this.baselineDoc && (this.baselineDoc as any)[this.scope] as Partial<RateConfig> | undefined;
    this.hasBaseline = !!(baselineScope && Array.isArray(baselineScope.factors) && baselineScope.factors.length > 0);

    // Same for the companion-cost document (what the network charges the SP).
    // We track two flags:
    //  - `hasCompanion`: the host signalled that a companion comparison
    //    is APPLICABLE in this editing context (it sets `companionLabel`
    //    when entering SP `rateConfig` mode). Drives the toggle's
    //    visibility — even an empty companion is worth surfacing so the
    //    user can flip it on and see "0" entries making it explicit that
    //    nothing is configured for this scope.
    //  - `hasCompanionFactors`: the companion has factors for the active
    //    scope. Used for the legend hint.
    this.hasCompanion = !!(this.companionLabel && this.companionLabel.length > 0);
    const companionScope = this.companionDoc && (this.companionDoc as any)[this.scope] as Partial<RateConfig> | undefined;
    this.hasCompanionFactors = !!(companionScope && Array.isArray(companionScope.factors) && companionScope.factors.length > 0);

    // Build rows: (enum combo × numberUp bucket). Each row evaluates a
    // FLAT cells array sized `pageColumns.length × tierColumns.length`,
    // with pages on the OUTER dimension (cell `pageIdx * T + tierIdx`).
    // `numberUp` is a DERIVED propertyCharacteristic (computed from the
    // item's body width/height, not stored on the item). When a factor
    // is conditioned on it we still need to surface it as a row axis,
    // so we synthesize `width`/`height` per row that classifies into the
    // chosen bucket via {@link computeNumberUp}. When no factor uses
    // `numberUp` we collapse this dimension to a single sentinel `''`
    // entry so the existing combo enumeration is unchanged.
    const numberUpBuckets: NumberUp[] = this.numberUpIsRelevant ? NUMBER_UP_VALUES : [];
    const numberUpAxis: (NumberUp | '')[] = numberUpBuckets.length > 0 ? numberUpBuckets.slice() : [''];
    const rows: PreviewRow[] = [];
    for (const combo of combos) {
      for (const nu of numberUpAxis) {
        // Pick {width, height} so `computeNumberUp` returns `nu`.
        // 4UP: both dimensions ≤ break (defaults already comply).
        // 2UP: bump width past the break — keeps height comfortably
        // within bounds so the synthesized item is still printable.
        const width  = nu === '2UP' ? 504 : PREVIEW_ITEM_DEFAULTS.width;   // 504pt = 7"
        const height = nu === '2UP' ? PREVIEW_ITEM_DEFAULTS.height : PREVIEW_ITEM_DEFAULTS.height;
        // Row-level `item` template: every page-column reuses this base
        // and overrides `pages` per cell when evaluating, so we don't
        // proliferate items per page-count.
        const item: PreviewItem = {
          ...PREVIEW_ITEM_DEFAULTS,
          width,
          height,
          // `pages` here is the LEGACY single-row representative — for
          // the row-attached breakdown click path we still hand this
          // value to evaluator paths that don't get the per-cell pages
          // override (none today, but keeps the field consistent).
          pages: this.pageColumns[0].pages,
          interiorType: combo.interiorType || 'GRAY',
          bindingType:  combo.bindingType  || 'PERFECT',
          laminateType: combo.laminateType || 'GLOSS',
          paperType:    combo.paperType    || '',
          packageType:  combo.packageType  || '',
          // Forward the facility token onto the synthetic item so
          // `valueForCharacteristic('facility', …)` resolves it
          // during per-book evaluation (the per-item evaluator path
          // reads `item.facility`; the order-level path reads
          // `OrderRateInput.facility`).
          facility:     combo.facility     || ''
        };
        const labels = this.enumColumns.map(p => {
          const raw = combo[p] || '';
          const d = describeCharacteristicValue(p, raw, this.paperTypeService, this.packageTypeService, this.facilityService);
          return {
            property: p,
            value: raw,
            display: d.label || raw || '—',
            tooltip: d.tooltip
          };
        });
        const cells: PreviewCell[] = [];
        for (const pc of this.pageColumns) {
          // Page-column-specific item: same combo + numberUp dimensions
          // but the page count varies — the evaluator reads `item.pages`
          // for `PER_NUMERIC_PROPERTY` factors targeting 'pages' (and
          // any group keyed on `pages` ranges), so this is the correct
          // place to inject the per-cell page count.
          const itemForPages: PreviewItem = {...item, pages: pc.pages};
          const currentBreaks = RateConfigEvaluator.evaluateBreaks(itemForPages, this.doc, this.scope, evalTiers);
          const baselineBreaks = this.hasBaseline
            ? RateConfigEvaluator.evaluateBreaks(itemForPages, this.baselineDoc, this.scope, evalTiers)
            : null;
          // Evaluate every peer doc once per page-column so the per-tier
          // loop below can pull each peer's result by tier index.
          // The companion-cost document (e.g. SP `serviceProviderChargedRateConfig`)
          // is rendered as a SYNTHETIC PEER prepended to the user-supplied
          // peer list when {@link showCompanionAsPeer} is on — that way
          // the network-cost comparison uses the same per-cell line format
          // as facility peers, and the user sees the actual cost value
          // (not just the margin) on every cell.
          const effectivePeerSources: { label: string; doc: RateConfigDocument }[] = [];
          if (this.hasCompanion && this.showCompanionAsPeer) {
            // Even when the companion has no factors for this scope, render
            // it as a synthetic peer with `0`/empty values so the user sees
            // an explicit comparison line saying "the network charges
            // nothing for this combo" — matches the "show inherited values"
            // policy elsewhere in the editor (don't lie by hiding).
            effectivePeerSources.push({label: this.effectiveCompanionLabel, doc: this.companionDoc});
          }
          for (const p of (this.peerDocs || [])) {
            if (p && p.doc) { effectivePeerSources.push(p); }
          }
          const peerBreaksList: { label: string; results: PreviewResult[] }[] = effectivePeerSources
            .map(p => ({
              label: p.label || '',
              results: RateConfigEvaluator.evaluateBreaks(itemForPages, p.doc, this.scope, evalTiers).map(b => b.result)
            }));
          for (let i = 0; i < currentBreaks.length; i++) {
            const b = currentBreaks[i];
            const cur = b.result;
            const base = baselineBreaks ? baselineBreaks[i].result : null;
            const peerDeltas: PreviewPeerDelta[] = peerBreaksList
              .map(pb => this.computePeerDelta(pb.label, cur, pb.results[i] || null))
              .filter((d): d is PreviewPeerDelta => !!d);
            const delta = this.computeDelta(cur, base);
            const cell: PreviewCell = {
              current: cur,
              delta,
              peerDeltas,
              display: '',
              tooltip: '',
              tooltipEnabled: false,
            };
            cell.display = this.cellDisplay(cur);
            cell.tooltip = this.cellTooltip(cell);
            cell.tooltipEnabled = !!((cur && cur.infeasible) || delta || peerDeltas.length > 0);
            cells.push(cell);
          }
        }
        rows.push({
          item,
          labels,
          numberUpLabel: nu || '',
          cells
        });
      }
    }
    this.rows = rows;
    this.rebuildColumnFilterOptions();
    this.applyFilters();
  }

  /**
   * Refresh {@link columnFilterOptions} from the current {@link rows}.
   * Preserves any pre-existing selection in {@link columnFilters} that
   * still corresponds to a value present in the new rows (so flipping
   * scope or page-counts doesn't silently drop user filters when the
   * value is still meaningful).
   */
  private rebuildColumnFilterOptions(): void {
    const out: { property: ItemEnumProperty; values: { raw: string; display: string }[] }[] = [];
    for (const p of this.enumColumns) {
      const seen: { [k: string]: string } = {};
      for (const r of this.rows) {
        const lab = r.labels.find(l => l.property === p);
        if (!lab) { continue; }
        if (!(lab.value in seen)) { seen[lab.value] = lab.display; }
      }
      const values = Object.keys(seen).map(raw => ({raw, display: seen[raw]}));
      out.push({property: p, values});
      // Prune any stale selections to keep the dropdown UX honest.
      const current = this.columnFilters[p];
      if (current && current.length > 0) {
        const allowed = new Set(values.map(v => v.raw));
        this.columnFilters[p] = current.filter(v => allowed.has(v));
      }
    }
    this.columnFilterOptions = out;
  }

  /**
   * Recompute {@link filteredRows} from {@link rows} + the current
   * filter state. Called whenever rows are rebuilt, a column filter
   * changes, or the hide-empty toggle flips — never from a template
   * binding (so change detection is cheap).
   *
   * Indices into {@link rows} are preserved via the `rowIndex` field
   * so cell clicks can map back to the original row for breakdown
   * computation.
   */
  private applyFilters(): void {
    const out: { row: PreviewRow; rowIndex: number }[] = [];
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      let pass = true;
      for (const p of this.enumColumns) {
        const sel = this.columnFilters[p];
        if (!sel || sel.length === 0) { continue; }
        const lab = row.labels.find(l => l.property === p);
        if (!lab || sel.indexOf(lab.value) < 0) { pass = false; break; }
      }
      if (!pass) { continue; }
      if (this.hideEmptyRows) {
        const anyFinite = row.cells.some(c => !(c.current && c.current.infeasible));
        if (!anyFinite) { continue; }
      }
      out.push({row, rowIndex: i});
    }
    this.filteredRows = out;
    // `totalColumns` is the colspan for the inline-breakdown sub-row.
    // After the pages→column migration the data-column count is the
    // FLATTENED `pageColumns * tierColumns` (no separate "Pages" cell
    // on each row anymore).
    this.totalColumns = this.enumColumns.length
      + (this.numberUpIsRelevant ? 1 : 0)
      + this.pageColumns.length * this.tierColumns.length;
  }

  /** trackBy for the filtered-rows *ngFor — keeps Angular from re-rendering
   *  every `<tr>` when a single filter flips. */
  trackFilteredRow = (_i: number, fr: { rowIndex: number }) => fr.rowIndex;
  /** trackBy for the per-row cells *ngFor. */
  trackCell = (i: number) => i;

  /** Filter dropdown options for one column. Returns `[]` when the column
   *  has 0 or 1 distinct value (no point offering a filter). */
  getFilterOptions(p: ItemEnumProperty): { raw: string; display: string }[] {
    const entry = this.columnFilterOptions.find(e => e.property === p);
    return entry ? entry.values : [];
  }

  /** Update the multi-select filter for one enum column. */
  onColumnFilterChange(p: ItemEnumProperty, values: string[]): void {
    this.columnFilters[p] = (values || []).slice();
    this.applyFilters();
    // If the currently-selected row is filtered out, dismiss the breakdown
    // so we don't leave a stale highlight that can't be seen.
    if (this.selectedCell) {
      const stillVisible = this.filteredRows.some(fr => fr.rowIndex === this.selectedCell!.rowIndex);
      if (!stillVisible) { this.clearBreakdown(); }
    }
  }

  /** Toggle the "hide rows where every cell is ∞" filter. */
  onHideEmptyRowsToggle(checked: boolean): void {
    this.hideEmptyRows = !!checked;
    this.applyFilters();
    if (this.selectedCell) {
      const stillVisible = this.filteredRows.some(fr => fr.rowIndex === this.selectedCell!.rowIndex);
      if (!stillVisible) { this.clearBreakdown(); }
    }
  }

  /** Reset every per-cell-breakdown field together so we never leave
   *  the panel in a half-cleared state where the grouped buckets
   *  array is empty but the totals still describe the previous cell. */
  private clearBreakdown(): void {
    this.selectedCell = null;
    this.selectedBreakdownGroups = [];
    this.selectedBreakdownTotals = null;
    this.selectedCellLabel = '';
  }

  /** Allowed axis values for one enum-valued propertyCharacteristic. */
  private valuesFor(p: ItemEnumProperty): string[] {
    if (p === 'paperType') {
      const list = (this.paperTypeService.objects || []).map(pt => '' + pt.ID);
      // Avoid an explosive matrix when no paperTypes have loaded yet — bail to one empty
      // value so the rest of the row still computes. (rebuild() will rerun on load.)
      return list.length > 0 ? list : [''];
    }
    if (p === 'packageType') {
      const list = (this.packageTypeService.objects || []).map(pt => '' + pt.ID);
      // Same on-demand-loading guard as paperType above — fall back to a
      // single empty value while the service hasn't loaded yet so the
      // preview still renders something. rebuild() reruns on load.
      return list.length > 0 ? list : [''];
    }
    if (p === 'facility') {
      // Facilities are dynamic (live FacilityService catalog). Filter
      // inactive rows out so the preview doesn't surface retired
      // facilities as columns — the editor's group-builder applies
      // the same filter, so the value set stays consistent across
      // authoring and demonstration. Same on-demand-loading guard as
      // paperType / packageType: a single empty fallback while the
      // service hasn't loaded yet (rebuild() reruns once data arrives).
      const list = (this.facilityService.objects || [])
        .filter(f => f && f.active !== false)
        .map(f => '' + f.ID);
      return list.length > 0 ? list : [''];
    }
    return ENUM_PROPERTY_VALUES[p] || [];
  }

  /** Human-friendly label for an enum value (paperType / packageType / facility IDs resolve to
   *  the type's `name` / friendlyName, carrierServiceLevel IDs resolve to "<carrier> — <serviceLevel>"). */
  labelForEnumValue(p: ItemEnumProperty, raw: string): string {
    if (!raw) { return '—'; }
    const d = describeCharacteristicValue(p, raw, this.paperTypeService, this.packageTypeService, this.facilityService);
    return d.label || raw;
  }

  /** Extended tooltip for an enum value (e.g. PackageType dimensions + carrier, or Facility city/state). Empty when none applies. */
  tooltipForEnumValue(p: ItemEnumProperty, raw: string): string {
    if (!raw) { return ''; }
    return describeCharacteristicValue(p, raw, this.paperTypeService, this.packageTypeService, this.facilityService).tooltip;
  }

  /** Header label for the enum-property column. */
  columnLabel(p: ItemEnumProperty): string {
    switch (p) {
      case 'bindingType':  return LanguagePipe.get('Binding type', 'compressSpaces');
      case 'interiorType': return LanguagePipe.get('Interior type', 'compressSpaces');
      case 'laminateType': return LanguagePipe.get('Laminate type', 'compressSpaces');
      case 'paperType':    return LanguagePipe.get('Paper type', 'compressSpaces');
      case 'packageType':  return LanguagePipe.get('Package type', 'compressSpaces');
      case 'facility':     return LanguagePipe.get('Facility', 'compressSpaces');
      // carrierServiceLevel is order-level and not surfaced as a per-book column.
      case 'carrierServiceLevel': return LanguagePipe.get('Carrier service level', 'compressSpaces');
    }
  }

  /**
   * Cell display for one (row, tier) pair. Infeasible cells show "∞";
   * normal cells show unitCost with the per-order portion appended in
   * parentheses when non-zero.
   */
  cellDisplay(r: PreviewResult): string {
    if (!r) { return ''; }
    if (r.infeasible) { return '∞'; }
    if (r.perOrderCost && r.perOrderCost !== 0) {
      return this.localizeNumber(r.unitCost) + ' (+' + this.localizeNumber(r.perOrderCost) + ')';
    }
    return this.localizeNumber(r.unitCost);
  }

  /** Tooltip for a cell — explains infeasibility and/or the delta vs. baseline and margin vs. companion. */
  cellTooltip(cell: PreviewCell): string {
    if (!cell) { return ''; }
    const parts: string[] = [];
    const r = cell.current;
    if (r && r.infeasible && r.infeasibleReason) {
      const reason = r.infeasibleReason;
      parts.push(LanguagePipe.get('Infeasible: factor ', 'compressSpaces')
        + reason.factorId + ' (' + reason.characteristics.join(', ') + ' = ' + reason.comboKey + ')');
    }
    if (cell.delta) {
      const verb = cell.delta.direction === 'up' ? LanguagePipe.get('Increased by', 'compressSpaces')
                 : cell.delta.direction === 'down' ? LanguagePipe.get('Decreased by', 'compressSpaces')
                 : cell.delta.direction === 'new' ? LanguagePipe.get('Newly priced (was infeasible)', 'compressSpaces')
                 : LanguagePipe.get('Newly infeasible (was priced)', 'compressSpaces');
      const detail = cell.delta.absLabel + (cell.delta.pctLabel ? ' (' + cell.delta.pctLabel + ')' : '');
      parts.push(verb + ' ' + detail + ' ' + LanguagePipe.get('vs. last save.', 'compressSpaces'));
    }
    return parts.join('  ');
  }

  // ── Per-cell breakdown ──────────────────────────────────────────

  /**
   * Click handler for a preview cell. Computes a step-by-step
   * breakdown of how the cell's value was derived (every factor's
   * matched cell key + contribution + per-factor minimum bumps,
   * plus global minimums and percentage discount when they apply)
   * and surfaces it in a detail panel under the table.
   *
   * Clicking the same cell again deselects it (toggle behavior).
   */
  onCellClick(rowIndex: number, cellIndex: number): void {
    if (this.selectedCell && this.selectedCell.rowIndex === rowIndex
        && this.selectedCell.cellIndex === cellIndex) {
      this.clearBreakdown();
      return;
    }
    const row = this.rows[rowIndex];
    if (!row) { return; }
    // Decode the flat cell index back into its (pageColumn, tierColumn)
    // coordinate. The cells array is in row-major order over the
    // `pageColumns × tierColumns` grid (see `rebuild()`).
    const tierCount = Math.max(1, this.tierColumns.length);
    const pageIdx = Math.floor(cellIndex / tierCount);
    const tierIdx = cellIndex % tierCount;
    const pc = this.pageColumns[pageIdx];
    const tier = this.tierColumns[tierIdx];
    if (!pc || !tier) { return; }
    // The breakdown needs an item with the per-cell page count applied —
    // the row's stored `item.pages` is the first page-column's value,
    // which would lie about the contribution for any cell in a later
    // page-column.
    const itemForCell: PreviewItem = {...row.item, pages: pc.pages};
    const breakdown = RateConfigEvaluator.evaluateItemBreakdown(
      itemForCell, tier.minQty, this.doc, this.scope);
    this.selectedCell = {rowIndex, cellIndex};
    this.selectedBreakdownGroups = this.buildBreakdownGroups(breakdown.steps);
    // Cell totals shown beneath the grouped breakdown so the user can
    // confirm that the per-bucket subtotals (plus any finalize
    // adjustments) reconcile against the value rendered in the table
    // cell itself.
    const r = breakdown.result;
    this.selectedBreakdownTotals = r ? {
      unitCost: r.unitCost,
      perOrderCost: r.perOrderCost,
      totalCost: r.totalCost,
      quantity: tier.minQty,
      infeasible: !!r.infeasible
    } : null;
    // Compose a human-readable label like "GRAY · COIL · 4UP · 200pp · 1–19"
    const parts: string[] = [];
    for (const l of row.labels) { if (l.display) { parts.push(l.display); } }
    if (this.numberUpIsRelevant && row.numberUpLabel) { parts.push(row.numberUpLabel); }
    if (this.pagesIsRelevant && pc.label) { parts.push(pc.label + 'pp'); }
    parts.push(tier.label);
    this.selectedCellLabel = parts.join(' · ');
  }

  /**
   * Bucket {@link BreakdownStep}s by `factorKind` into ordered groups
   * for the per-cell panel, AND fold each factor's per-group steps
   * into a single {@link BreakdownFactor} that the template can
   * render as one inline math expression.
   *
   * Factor-kind buckets render in {@link FACTOR_KIND_ORDER}
   * (Per order → Per item → Per book → Per page → Per package),
   * followed by a `finalize` bucket holding the synthetic engine
   * steps (global minimums, percentage discount, infeasibility
   * short-circuit). Empty buckets are omitted so the panel only
   * shows what actually contributed.
   *
   * Within a factor-kind bucket, every {@link BreakdownFactor}
   * folds together the per-group "line item" steps the engine
   * emitted for a single `RateFactor.id` PLUS its trailing roll-up
   * step. The roll-up step is where the engine reports the factor's
   * `multiplier` / `rawContribution` / `perItemMinimum` /
   * `contribution` — i.e. the "right-hand side" of the inline
   * expression — so we attach those onto the factor. Per-group
   * "line item" steps become {@link BreakdownFactorPart} entries
   * that render as the additive `[chars=key] amount + [chars=key] amount`
   * left-hand side of the expression.
   *
   * For PER_PACKAGE factors the engine emits one per-package
   * iteration step per element of `OrderRateInput.packageTypes[]`
   * before the roll-up; those iteration steps become parts whose
   * `perPackageNote` carries "Package N of M: packageType=X" so the
   * inline math reads as a sum of per-package amounts.
   */
  private buildBreakdownGroups(steps: BreakdownStep[]): BreakdownGroup[] {
    if (!steps || steps.length === 0) { return []; }
    // First pass: split steps by bucket so we can independently fold
    // each kind's factor-attributable steps into per-factor blocks.
    type BucketKey = BreakdownGroup['kind'];
    const stepsByBucket = new Map<BucketKey, BreakdownStep[]>();
    for (const s of steps) {
      const key: BucketKey = s.factorKind === 'config' ? 'finalize' : s.factorKind;
      let arr = stepsByBucket.get(key);
      if (!arr) { arr = []; stepsByBucket.set(key, arr); }
      arr.push(s);
    }
    // Build BreakdownGroups in the preferred order.
    const orderedKeys: BucketKey[] = [...FACTOR_KIND_ORDER, 'finalize'];
    const out: BreakdownGroup[] = [];
    for (const k of orderedKeys) {
      const bucketSteps = stepsByBucket.get(k);
      if (!bucketSteps || bucketSteps.length === 0) { continue; }
      if (k === 'finalize') {
        // Finalize steps stay as raw steps — they describe net
        // adjustments to the already-summed running totals (no
        // groupSum × multiplier shape).
        out.push({
          kind: k,
          label: this.breakdownGroupLabel(k),
          factors: [],
          finalizeSteps: bucketSteps,
          subtotalTarget: 'finalize',
          subtotal: 0
        });
        continue;
      }
      const factors = this.foldStepsIntoFactors(bucketSteps);
      // Bucket subtotal sums each factor's final `contribution` (so
      // multi-group factors contribute once, not once-per-group).
      let subtotal = 0;
      for (const fb of factors) { subtotal += fb.contribution; }
      const subtotalTarget: 'unit' | 'perOrder' =
        (k === 'PER_ORDER' || k === 'PER_PACKAGE') ? 'perOrder' : 'unit';
      out.push({
        kind: k,
        label: this.breakdownGroupLabel(k),
        factors,
        finalizeSteps: [],
        subtotalTarget,
        subtotal
      });
    }
    return out;
  }

  /**
   * Fold the engine's per-factor steps inside one bucket into
   * {@link BreakdownFactor} blocks. The engine emits, per factor:
   *  - 0..N per-group "line item" steps (one per matched group when
   *    the factor has more than one group);
   *  - 1 roll-up step carrying `matchedAmount` / `multiplier` /
   *    `rawContribution` / `perItemMinimum` / `contribution`.
   * Single-group factors collapse the per-group line and the roll-up
   * into a single step (so there's no separate line item to fold).
   *
   * For PER_PACKAGE factors the engine emits one per-package
   * iteration step per element of `packageTypes[]` plus the roll-up
   * — we fold those iterations into per-package parts.
   *
   * The engine SUPPRESSES the trailing roll-up when a multi-group
   * factor has zero net contribution AND no explanatory note (a
   * size optimisation in {@link RateConfigEvaluator.evaluateItemBreakdown}).
   * For that case we synthesize a zero-contribution roll-up so the
   * factor still surfaces in the UI as "matched these groups for a
   * sum of zero → +0 cost" rather than vanishing.
   */
  private foldStepsIntoFactors(stepsInBucket: BreakdownStep[]): BreakdownFactor[] {
    // Group steps by factorId in first-seen order so the visible
    // ordering preserves the document's factor authoring order.
    const order: string[] = [];
    const byFactor = new Map<string, BreakdownStep[]>();
    for (const s of stepsInBucket) {
      if (!byFactor.has(s.factorId)) {
        order.push(s.factorId);
        byFactor.set(s.factorId, []);
      }
      byFactor.get(s.factorId)!.push(s);
    }
    const out: BreakdownFactor[] = [];
    for (const fid of order) {
      const fs = byFactor.get(fid) || [];
      if (fs.length === 0) { continue; }
      // PER_PACKAGE factors have a distinctive nested shape (per-
      // package iteration steps, each with optional per-group inner
      // steps, plus a final roll-up across all packages). Delegate
      // to a dedicated fold so the rest of this method stays focused
      // on the per-book / per-order kinds.
      if (fs[0].factorKind === 'PER_PACKAGE') {
        const folded = this.foldPerPackageFactor(fs);
        if (folded) { out.push(folded); }
        continue;
      }

      // Identify the roll-up step. The engine's per-group "line item"
      // steps have a non-empty groupCharacteristics list AND
      // `matchedAmount === contribution` AND no `multiplier` /
      // `perItemMinimum`. A roll-up step is either:
      //  - the only step for the factor (single-group factor folded
      //    its per-group line into the roll-up), or
      //  - the trailing step that introduces multiplier / minimum /
      //    a sum-shaped matchedAmount.
      // When the engine suppressed the roll-up (multi-group, zero
      // contribution, no note), every step in `fs` is a per-group
      // line item; we synthesize a zero-contribution roll-up.
      const last = fs[fs.length - 1];
      const lastIsPerGroupLine =
        fs.length > 1 &&
        last.multiplier === undefined &&
        last.perItemMinimum === undefined &&
        Array.isArray(last.groupCharacteristics) &&
        last.groupCharacteristics.length > 0 &&
        typeof last.matchedAmount === 'number' &&
        last.matchedAmount === last.contribution;
      const hasExplicitRollUp = !lastIsPerGroupLine;
      const rollUp: BreakdownStep | undefined = hasExplicitRollUp ? last : undefined;
      const leadingLineItems = hasExplicitRollUp ? fs.slice(0, -1) : fs;

      const parts: BreakdownFactorPart[] = [];
      let baseAmountPart: BreakdownFactorPart | null = null;

      // Multi-group per-group steps.
      for (const s of leadingLineItems) {
        parts.push({
          characteristics: s.groupCharacteristics || [],
          matchedKey: s.matchedKey || '',
          inheritedFromKey: s.inheritedFromKey,
          amount: typeof s.matchedAmount === 'number' ? s.matchedAmount : 0
        });
      }

      if (rollUp && parts.length === 0) {
        // Single-group factor — the roll-up step IS the per-group
        // match. Surface it as a single part so the inline expression
        // reads `[chars=key] amount → +contribution`. Factors with
        // zero groups (baseAmount-only) have an empty
        // groupCharacteristics list AND no matchedKey — we flag those
        // as baseAmountPart instead so the template can skip the
        // `[…=…]` annotation.
        if (rollUp.groupCharacteristics && rollUp.groupCharacteristics.length > 0) {
          parts.push({
            characteristics: rollUp.groupCharacteristics,
            matchedKey: rollUp.matchedKey || '',
            inheritedFromKey: rollUp.inheritedFromKey,
            amount: typeof rollUp.matchedAmount === 'number' ? rollUp.matchedAmount : 0
          });
        } else {
          baseAmountPart = {
            characteristics: [],
            matchedKey: '',
            amount: typeof rollUp.matchedAmount === 'number' ? rollUp.matchedAmount : 0
          };
        }
      }

      // groupSum = the sum of per-group amounts. For single-group /
      // base-amount factors that's just the one part's amount; for
      // multi-group factors it's the sum (which the engine's roll-up
      // step also carries as `matchedAmount`).
      let groupSum: number;
      if (parts.length > 0) {
        groupSum = 0;
        for (const p of parts) { groupSum += p.amount; }
      } else if (baseAmountPart) {
        groupSum = baseAmountPart.amount;
      } else {
        groupSum = 0;
      }

      // For the roll-up step we trust the engine's own
      // `matchedAmount` / `multiplier` / `rawContribution` /
      // `contribution`. When the roll-up was suppressed we derive
      // everything from the parts (suppression only happens for
      // contribution === 0).
      const targetTotal: 'unit' | 'perOrder' = rollUp
        ? (rollUp.factorKind === 'PER_ORDER' || rollUp.factorKind === 'PER_PACKAGE' ? 'perOrder' : 'unit')
        : (last.factorKind === 'PER_ORDER' || last.factorKind === 'PER_PACKAGE' ? 'perOrder' : 'unit');
      const multiplier = rollUp ? rollUp.multiplier : undefined;
      const multiplierLabel = rollUp ? rollUp.multiplierLabel : undefined;
      const rawContribution = rollUp
        ? (typeof rollUp.rawContribution === 'number'
            ? rollUp.rawContribution
            : (typeof multiplier === 'number' ? groupSum * multiplier : groupSum))
        : groupSum; // suppression case → no multiplier/min, so raw === groupSum
      const contribution = rollUp
        ? (typeof rollUp.contribution === 'number' ? rollUp.contribution : rawContribution)
        : 0; // suppression only fires when contribution is 0
      const perItemMinimum = rollUp ? rollUp.perItemMinimum : undefined;

      out.push({
        factorId: rollUp ? rollUp.factorId : last.factorId,
        factorKind: rollUp ? rollUp.factorKind : last.factorKind,
        parts,
        baseAmountPart,
        groupSum,
        multiplier,
        multiplierLabel,
        rawContribution,
        perItemMinimum,
        contribution,
        targetTotal,
        note: rollUp ? rollUp.note : undefined
      });
    }
    return out;
  }

  /**
   * Fold the nested step list for a single PER_PACKAGE factor into
   * one {@link BreakdownFactor}. The engine emits, per factor:
   *  - For each package iteration `pi` (one per element of
   *    `packageTypes[]`):
   *    - For each group `gi` (multi-group only): one per-group line
   *      step with the package's note (`Package N of M: packageType=X`).
   *    - Multi-group only: one per-package sum step (`groupCharacteristics: []`,
   *      `matchedKey: packageTypeId`, `matchedAmount: packageSum`).
   *    - Single-group: just the one per-group line step (no sum step).
   *  - One final roll-up step (`groupCharacteristics: []`, no
   *    `matchedKey`, with `note: 'Summed across N packages.'`,
   *    `matchedAmount: factorSum`, `perItemMinimum`, `contribution`).
   *  - When `packageTypes[]` is empty, ONE roll-up step with the
   *    `'No packageTypes supplied'` note and contribution 0.
   *  - In the per-cell matrix path (`evaluateItemBreakdown`),
   *    a single placeholder step with `'Per-package factor — surfaced
   *    in the Packing preview section above'` note and contribution 0.
   *
   * The fold turns this into a {@link BreakdownFactor} whose `parts[]`
   * has one entry per package iteration, and (for multi-group
   * factors) each entry carries `subParts[]` holding the per-group
   * inner steps so the UI can render the package as
   * `(g1=amt + g2=amt) = packageSum`.
   */
  private foldPerPackageFactor(fs: BreakdownStep[]): BreakdownFactor | undefined {
    if (!fs || fs.length === 0) { return undefined; }
    // Pull off the final roll-up (the only step with no `matchedKey`
    // AND that's the LAST one). The engine always emits a roll-up
    // (even when packageTypes is empty or when this is the placeholder
    // step from `evaluateItemBreakdown`), so `last` is always it.
    const rollUp = fs[fs.length - 1];
    const interior = fs.slice(0, -1);

    // Walk the interior steps and group them by package iteration.
    // Steps with `groupCharacteristics.length > 0` are per-group line
    // items; the matching per-package sum step (same `note` prefix +
    // ` — package sum` suffix) closes the iteration. Single-group
    // factors omit the sum step — each package iteration is just one
    // per-group line whose note carries the package label.
    const parts: BreakdownFactorPart[] = [];
    let pendingGroupLines: BreakdownStep[] = [];
    for (const s of interior) {
      const note = s.note || '';
      const isSumStep = /— package sum$/.test(note)
        && Array.isArray(s.groupCharacteristics) && s.groupCharacteristics.length === 0;
      if (isSumStep) {
        // Close out the iteration: build a package part whose subParts
        // are the pending group-line items. The package label is the
        // note's `Package N of M: packageType=X` prefix.
        const pkgLabel = note.replace(/\s*—\s*package sum$/, '');
        const subParts: BreakdownFactorPart[] = pendingGroupLines.map(g => ({
          characteristics: g.groupCharacteristics || [],
          matchedKey: g.matchedKey || '',
          inheritedFromKey: g.inheritedFromKey,
          amount: typeof g.matchedAmount === 'number' ? g.matchedAmount : 0
        }));
        parts.push({
          characteristics: [],
          matchedKey: s.matchedKey || '',
          amount: typeof s.matchedAmount === 'number' ? s.matchedAmount : 0,
          perPackageNote: pkgLabel,
          subParts
        });
        pendingGroupLines = [];
        continue;
      }
      // Per-group line. For single-group factors there's no sum step
      // to close this — flush it directly as a top-level part with
      // the package note attached.
      pendingGroupLines.push(s);
    }
    // Any pending lines without a sum step belong to single-group
    // iterations — emit each as its own part.
    for (const g of pendingGroupLines) {
      parts.push({
        characteristics: g.groupCharacteristics || [],
        matchedKey: g.matchedKey || '',
        inheritedFromKey: g.inheritedFromKey,
        amount: typeof g.matchedAmount === 'number' ? g.matchedAmount : 0,
        perPackageNote: g.note
      });
    }

    // groupSum is the sum of every package iteration's amount.
    let groupSum = 0;
    for (const p of parts) { groupSum += p.amount; }

    const rawContribution = typeof rollUp.rawContribution === 'number'
      ? rollUp.rawContribution
      : groupSum;
    const contribution = typeof rollUp.contribution === 'number'
      ? rollUp.contribution
      : rawContribution;
    const perItemMinimum = rollUp.perItemMinimum;

    return {
      factorId: rollUp.factorId,
      factorKind: 'PER_PACKAGE',
      parts,
      baseAmountPart: null,
      groupSum,
      multiplier: undefined,
      multiplierLabel: undefined,
      rawContribution,
      perItemMinimum,
      contribution,
      targetTotal: 'perOrder',
      // Always preserve the roll-up note for PER_PACKAGE — it's the
      // "Summed across N packages." / "No packageTypes supplied" /
      // placeholder text that explains the totals (or absence
      // thereof). The `parts[]` list shows the per-package math; the
      // note tells the user what the engine actually did.
      note: rollUp.note
    };
  }

  /** Localized header label for a per-FactorKind (or finalize) bucket. */
  breakdownGroupLabel(kind: BreakdownGroup['kind']): string {
    switch (kind) {
      case 'PER_ORDER':            return LanguagePipe.get('Per order', 'compressSpaces');
      case 'PER_ITEM':             return LanguagePipe.get('Per item', 'compressSpaces');
      case 'PER_QUANTITY':         return LanguagePipe.get('Per book', 'compressSpaces');
      case 'PER_NUMERIC_PROPERTY': return LanguagePipe.get('Per page', 'compressSpaces');
      case 'PER_PACKAGE':          return LanguagePipe.get('Per package', 'compressSpaces');
      case 'finalize':             return LanguagePipe.get('Finalize', 'compressSpaces');
      default:                     return kind;
    }
  }

  /** Pre-formatted localized subtotal for a breakdown bucket. */
  breakdownGroupSubtotalLabel(g: BreakdownGroup): string {
    const sign = g.subtotal >= 0 ? '+' : '−';
    return sign + this.formatBreakdownAmount(g.subtotal);
  }

  /**
   * Format a numeric amount for the breakdown display: round to 4
   * decimal places (enough for per-page rates like `0.0125`) and
   * strip trailing zeros so larger values like `0.6000000000000001`
   * render as `0.6` rather than the float-precision noise the user
   * was seeing. Integers stay integers (`48` → `48`, not `48.0000`).
   *
   * Uses the same `toFixed(N).replace(/\.?0+$/, '')` pattern as
   * {@link computeDelta} / {@link computePeerDelta} so the breakdown
   * panel's numeric formatting matches the delta arrows shown in the
   * per-cell tooltip and peer comparison lines.
   *
   * Always returns the absolute value — callers prepend a `+` / `−`
   * sign so the leading sign and the digit run stay visually adjacent
   * in the math expression (no `localizeNumber()` zero-padding gap).
   *
   * Exposed as a bound arrow on `this` so the template can also call
   * it directly (e.g. inside the `ppPartInline` template that
   * iterates {@link BreakdownFactorPart}s for both the per-cell and
   * the packing-preview breakdown sections).
   */
  readonly formatBreakdownAmount = (n: any): string => {
    const num = Number(n);
    if (!isFinite(num)) { return '' + n; }
    // Round to 4 dp, then drop trailing zeros AND a lone trailing '.'
    // so `0.6000` → `0.6` and `1.0000` → `1`. Tiny per-page rates
    // (`0.0125`) survive intact because their 4th decimal place is
    // non-zero.
    const rounded = Math.abs(num).toFixed(4).replace(/\.?0+$/, '');
    return this.localizeNumber(rounded);
  }

  /**
   * Render one part's matched amount for the inline expression
   * (e.g. `0.40` or `0`). Just the number — characteristics / key /
   * inheritance live in the surrounding template markup so the math
   * line stays readable.
   */
  partAmountLabel(p: BreakdownFactorPart): string {
    return this.formatBreakdownAmount(p.amount);
  }

  /**
   * Pre-formatted final contribution for one factor (e.g. `+2.16`).
   * Used by the per-factor block's "→ +X.XX unit cost" tail.
   */
  factorContributionLabel(f: BreakdownFactor): string {
    const sign = f.contribution >= 0 ? '+' : '−';
    return sign + this.formatBreakdownAmount(f.contribution);
  }

  /**
   * Pre-formatted `rawContribution` (before perItemMinimum) for the
   * factor block's "→ {raw} → bumped to {min}" segment. Only used
   * when {@link factorMinBumped} is true.
   */
  factorRawContributionLabel(f: BreakdownFactor): string {
    const sign = f.rawContribution >= 0 ? '+' : '−';
    return sign + this.formatBreakdownAmount(f.rawContribution);
  }

  /**
   * Pre-formatted multiplier for PER_NUMERIC_PROPERTY factors
   * (e.g. `96` or `208` for signature-rounded pages). Undefined for
   * all other kinds.
   */
  factorMultiplierLabel(f: BreakdownFactor): string {
    return typeof f.multiplier === 'number' ? this.formatBreakdownAmount(f.multiplier) : '';
  }

  /**
   * Pre-formatted groupSum (the sum of part amounts) — only rendered
   * when the factor has more than one part, since for single-part
   * factors `groupSum === parts[0].amount` and surfacing it again
   * would be noise.
   */
  factorGroupSumLabel(f: BreakdownFactor): string {
    const sign = f.groupSum >= 0 ? '+' : '−';
    return sign + this.formatBreakdownAmount(f.groupSum);
  }

  /** True when the per-factor minimum actually bumped the contribution. */
  factorMinBumped(f: BreakdownFactor): boolean {
    return typeof f.perItemMinimum === 'number' && f.contribution !== f.rawContribution;
  }

  /** True when the factor has more than one part — controls whether
   *  the inline expression renders `(p1 + p2 + …) = groupSum`. */
  factorHasMultipleParts(f: BreakdownFactor): boolean {
    return f.parts.length > 1;
  }

  /**
   * True when the factor's `parts[]` represents PER_PACKAGE per-
   * package iterations and we want to lay them out VERTICALLY (one
   * package per line) rather than as a long inline sum. Triggers
   * whenever the factor is PER_PACKAGE and any part carries
   * `perPackageNote` (which is true for every real package
   * iteration; the empty-packageTypes / per-cell-matrix placeholder
   * factors have no parts at all). With three packages × two groups
   * the inline form would be ~200 characters of math on one line —
   * unreadable; the vertical form indents each package on its own
   * line so the user can scan top-to-bottom.
   */
  factorRendersPerPackageVertically(f: BreakdownFactor): boolean {
    if (f.factorKind !== 'PER_PACKAGE') { return false; }
    if (!f.parts || f.parts.length === 0) { return false; }
    return f.parts.some(p => !!p.perPackageNote);
  }

  /** Label for the companion-cost document; falls back to a generic "cost". */
  get effectiveCompanionLabel(): string {
    return this.companionLabel || LanguagePipe.get('cost', 'compressSpaces');
  }

  /**
   * Active facilities for the packing-preview "Facility" dropdown.
   * Filtered to `active !== false` so retired facilities don't surface
   * (matches the group-builder's authoring filter — see AGENTS.md /
   * `shared/ActiveFlags.md`). Each row is `{ id, label }` where `id`
   * is the stringified Facility.ID used as the engine token and
   * `label` is the human-readable friendlyName/name fallback.
   *
   * Refreshes the memoized {@link activeFacilityOptions} field. ONLY
   * mutates the field when the option list actually changed — the
   * `<mat-select>`'s `*ngFor` is reference-sensitive, so emitting a
   * new array on every `rebuild()` tick would re-key the options and
   * collapse the open overlay. See the field's doc-comment for the
   * freeze-bug rationale.
   */
  private refreshActiveFacilityOptions(): void {
    const next = (this.facilityService.objects || [])
      .filter(f => f && f.active !== false)
      .map(f => ({
        id: '' + f.ID,
        label: (f as any).friendlyName || (f as any).name || ('#' + f.ID)
      }));
    // Cheap structural-equality check: same length AND every id/label
    // matches positionally. Reference-replace only on real change so
    // the dropdown's option overlay doesn't re-mount on every tick.
    const cur = this.activeFacilityOptions;
    if (cur.length === next.length) {
      let same = true;
      for (let i = 0; i < cur.length; i++) {
        if (cur[i].id !== next[i].id || cur[i].label !== next[i].label) { same = false; break; }
      }
      if (same) { return; }
    }
    this.activeFacilityOptions = next;
  }

  // ── Delta vs. baseline ─────────────────────────────────────────────

  /**
   * Compute a {@link PreviewDelta} for one cell. Returns undefined when:
   *  - no baseline was provided (`base === null`);
   *  - both sides are infeasible (no information to convey);
   *  - the absolute change rounds to zero AND the cell wasn't infeasible.
   *
   * The "price" we compare is `unitCost + perOrderCost / preferredQty` —
   * but since `perOrderCost` is per-order and we don't know the order
   * quantity at preview time, we compare the unit costs only and report
   * any per-order change separately in the tooltip if it matters.
   */
  private computeDelta(current: PreviewResult, base: PreviewResult | null): PreviewDelta | undefined {
    if (!base) { return undefined; }

    const curInf = !!(current && current.infeasible);
    const baseInf = !!(base && base.infeasible);
    if (curInf && baseInf) { return undefined; }
    if (curInf && !baseInf) {
      return {direction: 'removed', absLabel: LanguagePipe.get('now ∞', 'compressSpaces'), pctLabel: ''};
    }
    if (!curInf && baseInf) {
      return {direction: 'new', absLabel: LanguagePipe.get('was ∞', 'compressSpaces'), pctLabel: ''};
    }
    const cur = (current && current.unitCost) || 0;
    const old = (base && base.unitCost) || 0;
    const diff = cur - old;
    // Tiny rounding noise from float arithmetic; suppress.
    if (Math.abs(diff) < 1e-6) { return undefined; }
    const direction: DeltaDirection = diff > 0 ? 'up' : 'down';
    const absLabel = (diff > 0 ? '+' : '−') + this.localizeNumber(Math.abs(diff).toFixed(4).replace(/\.?0+$/, ''));
    let pctLabel = '';
    if (Math.abs(old) > 1e-9) {
      const pct = (diff / old) * 100;
      pctLabel = (pct > 0 ? '+' : '−') + this.localizeNumber(Math.abs(pct).toFixed(1)) + '%';
    }
    return {direction, absLabel, pctLabel};
  }

  /**
   * Compute a {@link PreviewPeerDelta} for one (current, peer) pair.
   * Mirrors {@link computeDelta}'s logic but ALWAYS returns a record
   * (even when current and peer match exactly) so the user can see
   * the peer value at every cell — the whole point of side-by-side
   * comparison is to confirm parity, not just to surface differences.
   *
   * `direction` distinguishes profit/loss-style movements:
   *  - `up`               : peer charges MORE than current.
   *  - `down`             : peer charges LESS than current.
   *  - `same`             : exact parity (within rounding noise).
   *  - `currentInfeasible`: current refuses combo, peer has a price.
   *  - `peerInfeasible`   : peer refuses combo, current has a price.
   *  - `bothInfeasible`   : both refuse — peer line still rendered
   *                         (as `∞ = ∞`) for transparency.
   *
   * Returns `undefined` only when no peer was supplied at all (a
   * defensive nicety — the caller pre-filters falsy peers).
   */
  private computePeerDelta(label: string,
                            current: PreviewResult,
                            peer: PreviewResult | null): PreviewPeerDelta | undefined {
    if (!peer) { return undefined; }
    const curInf = !!(current && current.infeasible);
    const peerInf = !!(peer && peer.infeasible);
    if (curInf && peerInf) {
      return {label, direction: 'bothInfeasible', valueLabel: '∞', absLabel: '∞', pctLabel: ''};
    }
    if (curInf && !peerInf) {
      return {
        label, direction: 'currentInfeasible',
        valueLabel: this.localizeNumber((peer && peer.unitCost) || 0),
        absLabel: LanguagePipe.get('current ∞', 'compressSpaces'),
        pctLabel: ''
      };
    }
    if (!curInf && peerInf) {
      return {
        label, direction: 'peerInfeasible',
        valueLabel: '∞',
        absLabel: LanguagePipe.get('peer ∞', 'compressSpaces'),
        pctLabel: ''
      };
    }
    const cur = (current && current.unitCost) || 0;
    const p = (peer && peer.unitCost) || 0;
    const diff = p - cur;
    const valueLabel = this.localizeNumber(p);
    if (Math.abs(diff) < 1e-6) {
      return {label, direction: 'same', valueLabel, absLabel: '=', pctLabel: ''};
    }
    const direction: PeerDirection = diff > 0 ? 'up' : 'down';
    const absLabel = (diff > 0 ? '+' : '−') + this.localizeNumber(Math.abs(diff).toFixed(4).replace(/\.?0+$/, ''));
    let pctLabel = '';
    if (Math.abs(cur) > 1e-9) {
      const pct = (diff / cur) * 100;
      const psign = pct > 0 ? '+' : (pct < 0 ? '−' : '');
      pctLabel = psign + this.localizeNumber(Math.abs(pct).toFixed(1)) + '%';
    }
    return {label, direction, valueLabel, absLabel, pctLabel};
  }

  /** True when at least one peer doc is configured. Drives legend visibility. */
  get hasPeers(): boolean {
    return (this.peerDocs || []).some(p => p && p.doc);
  }

  /** Forward the facility multi-select change up to the host. */
  onSelectedFacilitiesChange(ids: number[]): void {
    this.selectedFacilityIds = (ids || []).slice();
    this.selectedFacilityIdsChange.emit(this.selectedFacilityIds);
  }

  /** Toggle the companion-as-peer rendering. Forwarded to the host so the
   *  preference can be persisted across re-renders / navigation. */
  onCompanionToggle(checked: boolean): void {
    this.showCompanionAsPeer = !!checked;
    this.showCompanionAsPeerChange.emit(this.showCompanionAsPeer);
    this.rebuild();
  }

  // ── Preview page counts (document-level field, hosted here for UX) ─────

  /** Comma-separated rendering of `doc._previewPageCounts`, defaulting to {@link DEFAULT_PREVIEW_PAGE_COUNTS}. */
  get pageCountsString(): string {
    const arr = this.doc && this.doc._previewPageCounts;
    return Array.isArray(arr) && arr.length > 0 ? arr.join(', ') : DEFAULT_PREVIEW_PAGE_COUNTS.join(', ');
  }

  /**
   * Handle user input on the page-counts field. Parses positive integers from
   * the comma/space-separated string; emits `undefined` when the parsed list
   * is empty so the parent removes the override and reverts to defaults.
   */
  onPageCountsInput(raw: string): void {
    if (this.readOnly) { return; }
    const parsed = (raw || '')
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .map(s => Number(s))
      .filter(n => isFinite(n) && n > 0 && Math.floor(n) === n);
    this.pageCountsChange.emit(parsed.length === 0 ? undefined : parsed);
  }

  // ── Packing preview (order-level evaluator entry point) ──────────────
  //
  // The per-book preview matrix above cannot meaningfully surface
  // PER_PACKAGE factors (it iterates per book, not per package) or
  // configurations that key on the order-level `carrierServiceLevel`
  // characteristic. The Packing preview surfaces those by accepting an
  // {@link OrderRateInput} directly: a free-form comma-separated
  // `packageTypes[]` text input, a `carrierServiceLevel` dropdown
  // populated from {@link CARRIER_SERVICE_LEVELS}, and a numeric
  // `quantity` (books in the order). The whole order is priced in one
  // call via {@link RateConfigEvaluator.evaluateOrder}.
  //
  // The section auto-shows only when the active scope has a
  // PER_PACKAGE factor or any group conditioned on `carrierServiceLevel`
  // (i.e. when there's something to demonstrate). For pure
  // production-rate scopes the section stays hidden.
  //
  // State fields are declared near the top of the class (see
  // `packingPreviewRelevant` etc.) to satisfy TSLint member-ordering.

  /** Re-evaluate the packing-preview result from the current inputs. */
  rebuildPackagePreview(): void {
    this.packagePreviewParseWarning = '';
    const tokens = (this.packagePreviewPackageTypesRaw || '')
      .split(/[\s,]+/)
      .map(s => s.trim())
      .filter(s => s.length > 0);
    // Allow purely-numeric strings (PackageType IDs) — the engine
    // stringifies them anyway. Reject anything else and warn so the
    // user knows the input was partially ignored.
    const clean: string[] = [];
    for (const t of tokens) {
      if (/^\d+$/.test(t)) { clean.push(t); }
      else {
        this.packagePreviewParseWarning =
          LanguagePipe.get('Ignored non-numeric tokens in packageTypes', 'compressSpaces') + `: "${t}"`;
      }
    }
    this.packagePreviewPackageTypes = clean;

    const qty = isFinite(this.packagePreviewQuantity) && this.packagePreviewQuantity > 0
      ? Math.floor(this.packagePreviewQuantity) : 1;
    const input: OrderRateInput = {
      quantity: qty,
      packageTypes: clean,
      carrierServiceLevel: this.packagePreviewCarrierServiceLevel || undefined,
      // Forward the facility token onto OrderRateInput so any group
      // conditioned on `facility` resolves (the engine reads from
      // `OrderRateInput.facility` first, falling back to
      // `OrderRateInput.item.facility` if unset — see
      // `valueForCharacteristic('facility', …)` in RateConfigEvaluator).
      // Empty string → undefined so the only-wildcard match path is
      // exercised rather than literally looking for `'facility=""'`.
      facility: this.packagePreviewFacility || undefined
    };
    if (!this.doc || !this.scope) { this.packagePreviewResult = null; this.packagePreviewBreakdownGroups = []; return; }
    try {
      const bd = RateConfigEvaluator.evaluateOrderBreakdown(input, this.doc, this.scope);
      this.packagePreviewResult = bd.result;
      this.packagePreviewBreakdownGroups = this.buildBreakdownGroups(bd.steps);
    } catch (e) {
      // evaluateOrder catches RateInfeasibleError and surfaces it via
      // the result's `infeasible` flag, so this branch is for genuine
      // bugs — surface them as a warning rather than crash the UI.
      console.error('Packing preview: evaluator threw unexpectedly', e);
      this.packagePreviewResult = null;
      this.packagePreviewBreakdownGroups = [];
      this.packagePreviewParseWarning = LanguagePipe.get('Evaluation error', 'compressSpaces') + `: ${(e as Error).message || e}`;
    }
  }

  /** Handler for the packageTypes text input. */
  onPackagePreviewPackageTypesInput(raw: string): void {
    this.packagePreviewPackageTypesRaw = raw || '';
    this.rebuildPackagePreview();
  }

  /** Handler for the carrier-service-level dropdown. */
  onPackagePreviewCarrierChange(level: string): void {
    this.packagePreviewCarrierServiceLevel = level || '';
    this.rebuildPackagePreview();
  }

  /** Handler for the facility dropdown in the packing preview. */
  onPackagePreviewFacilityChange(id: string): void {
    this.packagePreviewFacility = id || '';
    this.rebuildPackagePreview();
  }

  /** Handler for the quantity numeric input. */
  onPackagePreviewQuantityInput(raw: string | number): void {
    const n = Number(raw);
    this.packagePreviewQuantity = isFinite(n) && n > 0 ? n : 1;
    this.rebuildPackagePreview();
  }

  /** Format a currency value for display in the packing-preview result rows. */
  formatPreviewMoney(v: number | undefined | null): string {
    if (v == null || !isFinite(v as number)) { return '∞'; }
    return LanguagePipe.localizeNumber((v as number).toFixed(2));
  }
}




